/* eslint-disable react-hooks/exhaustive-deps */
import {
  DownloadOutlined,
  SearchOutlined,
  ShoppingCartOutlined,
  UserOutlined
} from "@ant-design/icons";
import { Select, Table, Card, Typography, Space, Tag, DatePicker, Button } from "antd";
import axios from "axios";
import dayjs from "dayjs";
import { useMemo, useEffect, useState } from "react";
import { CSVLink } from "react-csv";

// Assuming these utility functions are defined elsewhere and work as intended
import { selectFormOptionsNoFormatting } from "../../../utils/helper";
import { showErrorNotification, showSuccessNotification } from "../../../utils/notification";
import { clamp } from "../../../utils/responsiveHelpers";

const { Title, Text } = Typography;

const SALES_AVENUE_PAGES = [
  "/checkout",
  "/courses/public/1",
  "/courses/public/2",
  "/courses/public/3",
  "/courses/public/4"
];

const PotentialSalesLeadsDashBoard = ({ ROOT_URL }) => {
  const BASE_URL = "admin/payments/leads";

  const [potentialLeadsList, setPotentialLeadsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filter, setFilter] = useState({
    pages: SALES_AVENUE_PAGES,
    startDate: dayjs().subtract(1, "months").startOf("day"),
    endDate: dayjs().endOf("day")
  });

  const fetchPotentialLeadsList = () => {
    setLoading(true);
    axios
      .get(`${ROOT_URL}/${BASE_URL}/list`, {
        params: {
          filter
        }
      })
      .then((res) => {
        const { leadsList } = res.data;
        setPotentialLeadsList(leadsList);
        showSuccessNotification("Leads fetched successfully!");
      })
      .catch((err) => {
        showErrorNotification(err);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // Handler for DatePicker changes
  const handleDateChange = (dates) => {
    if (dates && dates.length === 2) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        startDate: dates[0].clone().startOf("day").toDate(),
        endDate: dates[1].clone().endOf("day").toDate()
      }));
    } else {
      setFilter((prevFilter) => ({
        ...prevFilter,
        startDate: null,
        endDate: null
      }));
    }
  };

  // Handler for Select changes
  const handlePageChange = (selectedValues) => {
    const newPages = selectedValues.length > 0 ? selectedValues : SALES_AVENUE_PAGES;
    setFilter((prevFilter) => ({
      ...prevFilter,
      pages: newPages
    }));
  };

  const potentialCourseFilterOptions = useMemo(() => {
    // Collect all unique course names from the "potentialCourses" field across all leads
    const allCourses = new Set();
    potentialLeadsList.forEach((lead) => {
      lead.potentialCourses
        ?.split(",")
        .map((c) => c.trim())
        .filter((c) => c.length > 0)
        .forEach((course) => allCourses.add(course));
    });

    // Format the unique names into Ant Design's required filter structure
    return Array.from(allCourses).map((course) => ({
      text: course,
      value: course
    }));
  }, [potentialLeadsList]);

  const csvData = useMemo(() => {
    // Define the headers and structure for the CSV file
    const headers = [
      { label: "S. No", key: "index" },
      { label: "User Name", key: "userName" },
      { label: "User Email", key: "userEmail" },
      { label: "User Country Code", key: "userCountryCode" },
      { label: "User Contact", key: "userContactNumber" },
      { label: "Joined Date", key: "userRegistrationDate" },
      { label: "Visited Pages", key: "visitedPages" },
      { label: "Total Visits", key: "totalVisits" },
      { label: "Last Activity", key: "lastVisit" },
      { label: "Purchased Courses", key: "purchasedCourses" },
      { label: "Potential Courses", key: "potentialCourses" }
    ];

    // Format the data for CSV
    const data = potentialLeadsList.map((lead) => ({
      ...lead,
      // Format dates for readability in CSV
      userRegistrationDate: dayjs(lead.userRegistrationDate).format("YYYY-MM-DD"),
      lastVisit: dayjs(lead.lastVisit).format("YYYY-MM-DD HH:mm:ss")
    }));

    return { headers, data };
  }, [potentialLeadsList]);

  useEffect(() => {
    fetchPotentialLeadsList();
  }, [filter]);

  const columns = [
    {
      title: "S. No",
      dataIndex: "index",
      key: "index",
      width: 70
    },
    {
      title: (
        <Space>
          <UserOutlined /> User Info
        </Space>
      ),
      key: "userInfo",
      render: (text, record) => (
        <Space direction="vertical" size={2}>
          <Text strong>{record.userName}</Text>
          <Text copyable type="secondary">
            {record.userEmail}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.85em" }}>
            Joined: {dayjs(record.userRegisteredDate).format("YYYY-MM-DD")}
          </Text>
          <Text type="secondary" style={{ fontSize: "0.85em" }}>
            Contact: {`${record.userCountryCode || "N/A"} ${record.userContactNumber || ""}`}
          </Text>
        </Space>
      ),
      sorter: (a, b) => a.userName.localeCompare(b.userName),
      width: 250
    },
    {
      title: (
        <Space>
          <SearchOutlined /> Pages Visited
        </Space>
      ),
      dataIndex: "visitedPages",
      key: "visitedPages",
      sorter: (a, b) => a.visitedPages.localeCompare(b.visitedPages),
      render: (text) =>
        text?.split(",").map((page, i) => (
          <Tag key={i} color={page.includes("checkout") ? "magenta" : "blue"}>
            {page.trim()}
          </Tag>
        )),
      width: 300
    },
    {
      title: "Total Visits",
      dataIndex: "totalVisits",
      key: "totalVisits",
      align: "center",
      sorter: (a, b) => a.totalVisits - b.totalVisits,
      width: 120
    },
    {
      title: "Last Activity",
      dataIndex: "lastVisit",
      key: "lastVisit",
      render: (date) => dayjs(date)?.format("MMM DD, HH:mm:ss"),
      sorter: (a, b) => dayjs(a.lastVisit).unix() - dayjs(b.lastVisit).unix(),
      defaultSortOrder: "descend",
      width: 180
    },
    {
      title: (
        <Space>
          <ShoppingCartOutlined /> Purchased Courses
        </Space>
      ),
      dataIndex: "purchasedCourses",
      key: "purchasedCourses",
      render: (text) => {
        const courses = text?.split(",").filter((c) => c.trim().length > 0) || [];
        if (courses.length === 0) return <Tag color="green">None</Tag>;

        return courses.map((course, i) => (
          <Tag key={i} color="success">
            {course.trim()}
          </Tag>
        ));
      },
      sorter: (a, b) => a.purchasedCourses.localeCompare(b.purchasedCourses),
      width: 250
    },
    {
      title: "Potential Courses",
      dataIndex: "potentialCourses",
      key: "potentialCourses",
      filters: potentialCourseFilterOptions,
      onFilter: (value, record) => record.potentialCourses.includes(value),
      render: (text) => {
        const courses = text?.split(",").filter((c) => c.trim().length > 0) || [];
        if (courses.length === 0) return <Tag color="gray">None (Owns All)</Tag>;

        return courses.map((course, i) => (
          <Tag key={i} color="volcano" bordered={false}>
            {course.trim()}
          </Tag>
        ));
      },
      sorter: (a, b) => a.potentialCourses.localeCompare(b.potentialCourses),
      width: 250
    }
  ];

  return (
    <div style={{ padding: clamp(12, 20) }}>
      <Card
        title={
          <Title level={4} style={{ margin: 0 }}>
            Potential Sales Leads Dashboard 🎯
          </Title>
        }
        bordered={false}
        extra={
          <Space wrap size="middle">
            <CSVLink
              data={csvData.data}
              headers={csvData.headers}
              filename={`sales_leads_${dayjs().format("YYYYMMDD_HHmmss")}.csv`}
              target="_blank"
            >
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                disabled={potentialLeadsList.length === 0 || loading}
              >
                Export {potentialLeadsList.length} Leads
              </Button>
            </CSVLink>
            <Space>
              <Text strong>Filter by Date:</Text>
              {/* 4. 🛠️ FIX: Use RangePicker and bind value to filter state */}
              <DatePicker.RangePicker
                style={{ width: "min(280px, 100%)" }}
                value={
                  filter.startDate && filter.endDate
                    ? [dayjs(filter.startDate), dayjs(filter.endDate)]
                    : []
                }
                onChange={handleDateChange}
                showTime={{ format: "HH:mm:ss" }}
                format="YYYY-MM-DD HH:mm:ss"
              />
            </Space>

            <Space>
              <Text strong>Filter by Page:</Text>
              <Select
                mode="multiple"
                placeholder="Select Page(s) or leave blank for All"
                options={selectFormOptionsNoFormatting(SALES_AVENUE_PAGES)}
                // Value is now bound to filter.pages
                value={filter.pages.length === SALES_AVENUE_PAGES.length ? [] : filter.pages}
                onChange={handlePageChange}
                showSearch
                allowClear
                style={{ width: "min(300px, 100%)" }}
              />
            </Space>
          </Space>
        }
        style={{ marginBottom: 20 }}
      >
        <Table
          columns={columns}
          dataSource={potentialLeadsList}
          loading={loading}
          bordered
          scroll={{ x: 1400 }} // Enable horizontal scroll and sticky header
          sticky
        />
      </Card>
    </div>
  );
};

export default PotentialSalesLeadsDashBoard;
